// pages/PersonalData/PersonalData.tsx
import React, { useContext, useEffect, useState } from "react";
import StepProgress, { Step } from './components/StepProgress/StepProgress';
import { ReactComponent as IconEmail } from 'assets/img/icons/icon_email_small.svg'; // À créer/importer
import { ReactComponent as IconPhone } from 'assets/img/icons/icon_phone.svg'; // À créer/importer
import { ReactComponent as IconCheck } from 'assets/img/icons/icon_Check.svg'; // À créer/importer
import { ReactComponent as IconLock } from 'assets/img/icons/icon_Lock.svg'; // À créer/importer
import './PersonalData.scss';
import { PersonalDataContext } from "features/personal-data";
import { UserContext } from "features/account";
import { useNavigate } from "react-router";
import Step1Choose from "./components/Step1Choose/Step1Choose";
import Step2Emails from "./components/Step2Emails/Step2Emails";
import Step3Phones from "./components/Step3Phones/Step3Phones";

const PersonalData: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const {
    personalDatas,
    loading,
    addPersonalDatas,
    updatePersonalData,
    deletePersonalData
  } = useContext(PersonalDataContext);
  const { userType } = useContext(UserContext);
  const navigate = useNavigate();

  // Rediriger si pas connecté
  useEffect(() => {
    if (!userType) {
      navigate('/connexion');
    }
  }, [userType, navigate]);

  // Filtrer les données existantes
  const existingEmails = personalDatas?.filter(d => d.type === 'email') || [];
  const existingPhones = personalDatas?.filter(d => d.type === 'phoneNumber') || [];

  // Définir les steps avec leur état (actif/désactivé)
  const steps: Step[] = [
    {
      id: 1,
      label: "Données à protéger",
      icon: <IconLock />,
      isActive: currentStep === 1,
      isCompleted: currentStep > 1
    },
    {
      id: 2,
      label: "Emails",
      icon: <IconEmail />,
      isActive: currentStep === 2,
      isCompleted: currentStep > 2,
      isDisabled: !selectedTypes.includes('email')
    },
    {
      id: 3,
      label: "Téléphones",
      icon: <IconPhone />,
      isActive: currentStep === 3,
      isCompleted: currentStep > 3,
      isDisabled: !selectedTypes.includes('phoneNumber')
    },
    {
      id: 4,
      label: "Confirmation",
      icon: <IconCheck />,
      isActive: currentStep === 4,
      isCompleted: currentStep > 4
    }
  ];

  // Fonctions pour gérer les emails
  const handleAddEmail = async (value: string) => {
    await addPersonalDatas([{ type: 'email', value }]);
  };

  const handleUpdateEmail = async (id: number, value: string) => {
    await updatePersonalData(id, value);
  };

  const handleDeleteEmail = async (id: number) => {
    await deletePersonalData(id);
  };

  const handleAddPhone = async (value: string) => {
    await addPersonalDatas([{ type: 'phoneNumber', value }]);
  };

  const handleUpdatePhone = async (id: number, value: string) => {
    await updatePersonalData(id, value);
  };

  const handleDeletePhone = async (id: number) => {
    await deletePersonalData(id);
  };

  // Navigation
  const goToStep = (step: number) => {
    // Vérifier si le step est accessible
    if (step === 2 && !selectedTypes.includes('email')) return;
    if (step === 3 && !selectedTypes.includes('phoneNumber')) return;
    if (step < currentStep) {
      setCurrentStep(step); // Permettre de revenir en arrière
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (selectedTypes.includes('phoneNumber')) {
        setCurrentStep(3);
      } else {
        setCurrentStep(4);
      }
    } else if (currentStep === 3) {
      setCurrentStep(4);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipStep = () => {
    if (currentStep === 2 && selectedTypes.includes('phoneNumber')) {
      setCurrentStep(3);
    } else {
      setCurrentStep(4);
    }
  };

  if (loading) {
    return (
      <div className="personal-data-page">
        <div className="loader-protectRoute" />
      </div>
    );
  }

  return (
    <div className="personal-data-page">
      <div className="personal-data-page__header">
        <h1 className="text-primary text-2xl bold">
          Protégez vos données personnelles
        </h1>
        <p className="text-muted-foreground mt-2">
          Créez votre dossier de protection en quelques étapes
        </p>
      </div>

      <StepProgress
        currentStep={currentStep}
        steps={steps}
        onStepClick={goToStep}
      />

      <div className="personal-data-page__content">
        {currentStep === 1 && (
          <Step1Choose
            selectedTypes={selectedTypes}
            onSelect={setSelectedTypes}
            onNext={nextStep}
          />
        )}

        {currentStep === 2 && selectedTypes.includes('email') && (
          <Step2Emails
            existingData={existingEmails}
            onAdd={handleAddEmail}
            onUpdate={handleUpdateEmail}
            onDelete={handleDeleteEmail}
            onNext={nextStep}
            onSkip={skipStep}
            onBack={prevStep}
          />
        )}

        {currentStep === 3 && selectedTypes.includes('phoneNumber') && (
          <Step3Phones
            existingData={existingPhones}
            onAdd={handleAddPhone}
            onUpdate={handleUpdatePhone}
            onDelete={handleDeletePhone}
            onNext={nextStep}
            onSkip={skipStep}
            onBack={prevStep}
          />
        )}

        {currentStep === 4 && (
          <div>Step 4 Confirmation (à venir)</div>
        )}
      </div>
    </div>
  );
};

export default PersonalData;